// coverageMatrix — the Learning objectives page as data (pen "Studio · Page
// · Learning objectives"). Pure: the outline's parts (studioPages.ts) and
// the project's objectives with their coverage (the `projectObjectives`
// read) are projected into
//  - the COLUMNS: one per objective, numbered in the project's order, each
//    with its state (the chips' four, `stateOf`);
//  - the ROWS: the outline's parts (same order and indent, the root left
//    out), each with one mark per column; a module's marks sum up its parts
//    (and its own), shown faint, however deep they lie (`within`: the
//    containers above each part, from the read; the outline may not list
//    every level);
//  - the COUNTS per state (the chips and the first sentence);
//  - what a column's card says: where it is taught, checked or planned, and
//    one next step.
// Marks, strongest first: checked (a quiz checks it) > taught > planned.
// The assistant's suggestions are not in the data yet: no "suggested" mark
// (a design gap, the default is no mark).

export type {
    CoverageColumn,
    CoverageFilter,
    CoverageMark,
    CoverageRow,
    CoverageState,
};
export {
    COVERAGE_STATES,
    columnsOf,
    countsOf,
    nextStepOf,
    rowsOf,
    shownColumns,
    stateOf,
};

import { exhaustSwitchCase } from "@bilda/utils";
import type { ProjectObjective } from "../../app/ports/project.js";
import type { OutlineRow, PageNodeKind } from "../StudioEditor/studioPages.js";

// The chips' four states, in the chips' order.
type CoverageState = "no_part_yet" | "not_checked" | "planned" | "covered";

const COVERAGE_STATES: readonly CoverageState[] = [
    "no_part_yet",
    "not_checked",
    "planned",
    "covered",
];

// A chip: every column, or one state's.
type CoverageFilter = "all" | CoverageState;

type CoverageMark = "checked" | "taught" | "planned" | "none";

// Taught and checked → covered; taught or checked (one of the two) → not
// checked (pen's "Not checked" also holds a check without teaching: a design
// gap, shown with the card's own words); else planned → planned; else none.
const stateOf = (o: ProjectObjective): CoverageState =>
    o.taught.length > 0 && o.checked.length > 0
        ? "covered"
        : o.taught.length + o.checked.length > 0
          ? "not_checked"
          : o.planned.length > 0
            ? "planned"
            : "no_part_yet";

type CoverageColumn = {
    // 1-based, in the project's order; kept when a chip narrows the columns.
    readonly number: number;
    readonly objective: ProjectObjective;
    readonly state: CoverageState;
};

const columnsOf = (
    objectives: readonly ProjectObjective[],
): readonly CoverageColumn[] =>
    objectives.map((objective, i) => ({
        number: i + 1,
        objective,
        state: stateOf(objective),
    }));

const shownColumns = (
    columns: readonly CoverageColumn[],
    filter: CoverageFilter,
): readonly CoverageColumn[] =>
    filter === "all" ? columns : columns.filter((c) => c.state === filter);

const countsOf = (
    columns: readonly CoverageColumn[],
): ReadonlyMap<CoverageState, number> =>
    new Map(
        COVERAGE_STATES.map((s) => [
            s,
            columns.filter((c) => c.state === s).length,
        ]),
    );

type CoverageRow = {
    readonly key: string;
    readonly id: string;
    readonly title: string;
    readonly kind: PageNodeKind;
    // 0 = directly in the root.
    readonly depth: number;
    // A module (or course) row: its marks sum up its parts.
    readonly summary: boolean;
    // One per column, in the columns' order.
    readonly marks: readonly CoverageMark[];
};

const markOf = (o: ProjectObjective, ids: readonly string[]): CoverageMark =>
    ids.some((id) => o.checked.includes(id))
        ? "checked"
        : ids.some((id) => o.taught.includes(id))
          ? "taught"
          : ids.some((id) => o.planned.includes(id))
            ? "planned"
            : "none";

// The rows below `i` that sit inside it (deeper, until one is not).
const insideOf = <R extends { readonly depth: number }>(
    rows: readonly R[],
    i: number,
): readonly R[] => {
    const at = rows[i];
    if (at === undefined) return [];
    const after = rows.slice(i + 1);
    const end = after.findIndex((r) => r.depth <= at.depth);
    return end === -1 ? after : after.slice(0, end);
};

// The outline's rows (unfolded) without the root, each with its marks.
const rowsOf = (
    outline: readonly OutlineRow[],
    columns: readonly CoverageColumn[],
    within: ReadonlyMap<string, readonly string[]>,
): readonly CoverageRow[] => {
    const deep = [...within];
    const parts = outline.filter(
        (r): r is OutlineRow & { readonly kind: PageNodeKind } =>
            r.kind !== "root",
    );
    return parts.map((row, i) => {
        const inside = insideOf(parts, i);
        const summary = row.kind === "module" || row.kind === "course";
        const ids = [
            row.id,
            ...inside.map((r) => r.id),
            ...deep.flatMap(([part, above]) =>
                above.includes(row.id) ? [part] : [],
            ),
        ];
        return {
            key: row.key,
            id: row.id,
            title: row.title,
            kind: row.kind,
            depth: Math.max(0, row.depth - 1),
            summary,
            marks: columns.map((c) =>
                markOf(c.objective, summary ? ids : [row.id]),
            ),
        };
    });
};

// The one next step a column's card offers, each case explicit.
type NextStep =
    | { readonly type: "add_part" }
    | { readonly type: "add_question"; readonly quiz: string }
    | { readonly type: "add_quiz" }
    | { readonly type: "none" };

// The quiz nearest the first part that teaches it: one in the module that
// holds that part, else the first in the outline.
const quizFor = (
    rows: readonly CoverageRow[],
    taughtIn: readonly string[],
): CoverageRow | undefined => {
    const isQuiz = (r: CoverageRow) => r.kind === "quiz";
    const at = rows.findIndex((r) => taughtIn.includes(r.id));
    const depth = rows[at]?.depth ?? 0;
    // The last module above it that is shallower: the one that holds it.
    const holder = rows.reduce(
        (h, r, i) => (i < at && r.summary && r.depth < depth ? i : h),
        -1,
    );
    return insideOf(rows, holder).find(isQuiz) ?? rows.find(isQuiz);
};

const nextStepOf = (
    column: CoverageColumn,
    rows: readonly CoverageRow[],
): NextStep => {
    switch (column.state) {
        case "no_part_yet":
            return { type: "add_part" };
        case "not_checked": {
            if (column.objective.checked.length > 0) return { type: "none" };
            const quiz = quizFor(rows, column.objective.taught);
            return quiz === undefined
                ? { type: "add_quiz" }
                : { type: "add_question", quiz: quiz.title };
        }
        case "planned":
        case "covered":
            return { type: "none" };
        default:
            return exhaustSwitchCase(column.state);
    }
};
